import concaveman from 'concaveman';

// Utility functions for creating area overlays from station locations
// Groups stations by area and creates polygon boundaries

// NOTE:
// Historically the area overlay drew a padded bounding-box around station clusters.
// The agrivoltaics UX now expects two main groups per field (Section A / Section B),
// each representing ~half the field polygon (≈1 ha each for a 2 ha farm polygon).

/**
 * Parse location description to extract area identifier
 * Examples:
 * - "North Field, Section A" -> { field: "North Field", area: "Section A" }
 * - "Solar panel row 1" -> { field: "Solar panel", area: "row 1" }
 * - "South greenhouse" -> { field: "South", area: "greenhouse" }
 */
export const parseLocationDescription = (description) => {
  if (!description) return { field: 'Unknown', area: 'Unknown' };

  // Pattern: "Block 001 - Campo Aperto" / "Block 001 - Retro Modulo"
  // Group by block so paired sensors remain together.
  const blockTypeMatch = description.match(/^(Block\s+\d{1,3})\s*-\s*(.+)$/i);
  if (blockTypeMatch) {
    return {
      field: blockTypeMatch[2].trim(),
      area: blockTypeMatch[1].trim(),
      fullArea: blockTypeMatch[1].trim()
    };
  }

  // Pattern: "Block 001"
  const simpleBlockMatch = description.match(/^Block\s+(\d{1,3})$/i);
  if (simpleBlockMatch) {
    const normalized = `Block ${String(simpleBlockMatch[1]).padStart(3, '0')}`;
    return {
      field: 'Block',
      area: normalized,
      fullArea: normalized
    };
  }
  
  // Pattern: "Section X - Campo Aperto" or "Section X - Some Type"
  // This matches the actual format from mockStationsData.js
  const sectionTypeMatch = description.match(/^(Section\s+[A-Z])\s*-\s*(.+)$/i);
  if (sectionTypeMatch) {
    return {
      field: sectionTypeMatch[2].trim(), // "Campo Aperto"
      area: sectionTypeMatch[1].trim(),   // "Section A"
      fullArea: sectionTypeMatch[1].trim() // Use just "Section A" for grouping
    };
  }
  
  // Pattern: "Section X" (simple format)
  const simpleSectionMatch = description.match(/^Section\s+([A-Z])$/i);
  if (simpleSectionMatch) {
    return {
      field: 'Section',
      area: `Section ${simpleSectionMatch[1]}`,
      fullArea: `Section ${simpleSectionMatch[1]}`
    };
  }
  
  // Pattern: "Master Station - Location"
  const masterStationMatch = description.match(/^Master\s+Station\s*-\s*(.+)$/i);
  if (masterStationMatch) {
    return {
      field: 'Master',
      area: 'Master Station',
      fullArea: 'Master Station'
    };
  }
  
  // Pattern: "Field Name, Section X" or "Field Name, Row X"
  const sectionMatch = description.match(/^(.+?),\s*(Section|Row)\s+([A-Z0-9]+)$/i);
  if (sectionMatch) {
    return {
      field: sectionMatch[1].trim(),
      area: `${sectionMatch[2]} ${sectionMatch[3]}`,
      fullArea: `${sectionMatch[2]} ${sectionMatch[3]}`
    };
  }
  
  // Pattern: "Field Name Row X"
  const rowMatch = description.match(/^(.+?)\s+(row|Row)\s+(\d+)$/i);
  if (rowMatch) {
    return {
      field: rowMatch[1].trim(),
      area: `Row ${rowMatch[3]}`,
      fullArea: `Row ${rowMatch[3]}`
    };
  }
  
  // Pattern: "Direction Field" or "Direction Feature"
  const simpleMatch = description.match(/^([A-Z][a-z]+)\s+(.+)$/);
  if (simpleMatch) {
    return {
      field: simpleMatch[1],
      area: simpleMatch[2],
      fullArea: description
    };
  }
  
  // Default: use entire description as area
  return {
    field: 'General',
    area: description,
    fullArea: description
  };
};

/**
 * Group stations by area
 */
export const groupStationsByArea = (stations) => {
  const areaGroups = {};
  
  stations.forEach(station => {
    const parsed = parseLocationDescription(station.locationDescription);
    const areaKey = parsed.fullArea || `${parsed.field} - ${parsed.area}`;
    
    if (!areaGroups[areaKey]) {
      areaGroups[areaKey] = {
        areaName: areaKey,
        field: parsed.field,
        area: parsed.area,
        stations: [],
        bounds: null
      };
    }
    
    areaGroups[areaKey].stations.push(station);
  });
  
  // Calculate bounds for each area
  Object.values(areaGroups).forEach(area => {
    if (area.stations.length > 0) {
      const lats = area.stations.map(s => s.locationLat);
      const lons = area.stations.map(s => s.locationLon);
      
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLon = Math.min(...lons);
      const maxLon = Math.max(...lons);
      
      // Expand bounds to create a visible polygon (add padding)
      const padding = 0.0003; // ~33 meters
      area.bounds = {
        minLat: minLat - padding,
        maxLat: maxLat + padding,
        minLon: minLon - padding,
        maxLon: maxLon + padding
      };
      
      // Count station types
      area.campoApertoCount = area.stations.filter(s => s.sensorType === 'campo_aperto').length;
      area.retroModuloCount = area.stations.filter(s => s.sensorType === 'retro_modulo').length;
      area.totalCount = area.stations.length;
    }
  });
  
  return areaGroups;
};

// ============================================================================
// Polygon splitting helpers (farm polygon -> Section A / Section B)
// ============================================================================

const EARTH_RADIUS_M = 6371008.8;

const toXYMeters = (lat, lon, lat0Rad) => {
  const latRad = (lat * Math.PI) / 180;
  const lonRad = (lon * Math.PI) / 180;
  return {
    x: EARTH_RADIUS_M * lonRad * Math.cos(lat0Rad),
    y: EARTH_RADIUS_M * latRad,
  };
};

export const approximatePolygonAreaM2 = (polygonLatLon) => {
  if (!polygonLatLon || polygonLatLon.length < 3) return 0;
  const lats = polygonLatLon.map(p => p[0]);
  const lat0 = lats.reduce((a, b) => a + b, 0) / lats.length;
  const lat0Rad = (lat0 * Math.PI) / 180;

  // Shoelace in meters using a local equirectangular projection
  let area2 = 0;
  for (let i = 0; i < polygonLatLon.length; i++) {
    const j = (i + 1) % polygonLatLon.length;
    const a = toXYMeters(polygonLatLon[i][0], polygonLatLon[i][1], lat0Rad);
    const b = toXYMeters(polygonLatLon[j][0], polygonLatLon[j][1], lat0Rad);
    area2 += a.x * b.y - b.x * a.y;
  }
  return Math.abs(area2) / 2;
};

const clipPolygonHalfPlane = (polygonLatLon, axis, threshold, keepLessOrEqual) => {
  if (!polygonLatLon || polygonLatLon.length < 3) return [];

  const getCoord = (p) => (axis === 'lon' ? p[1] : p[0]);
  const isInside = (p) => {
    const v = getCoord(p);
    return keepLessOrEqual ? v <= threshold : v >= threshold;
  };
  const intersect = (a, b) => {
    const aCoord = getCoord(a);
    const bCoord = getCoord(b);
    const denom = bCoord - aCoord;
    if (denom === 0) return null;
    const t = (threshold - aCoord) / denom;
    if (t < 0 || t > 1) return null;

    // Interpolate in lat/lon space (good enough for these small polygons)
    const lat = a[0] + t * (b[0] - a[0]);
    const lon = a[1] + t * (b[1] - a[1]);
    return [lat, lon];
  };

  const input = polygonLatLon;
  const output = [];

  for (let i = 0; i < input.length; i++) {
    const current = input[i];
    const prev = input[(i - 1 + input.length) % input.length];
    const currInside = isInside(current);
    const prevInside = isInside(prev);

    if (currInside) {
      if (!prevInside) {
        const p = intersect(prev, current);
        if (p) output.push(p);
      }
      output.push(current);
    } else if (prevInside) {
      const p = intersect(prev, current);
      if (p) output.push(p);
    }
  }

  // Remove duplicates that can arise at clip boundaries
  const cleaned = [];
  for (const p of output) {
    const last = cleaned[cleaned.length - 1];
    if (!last || last[0] !== p[0] || last[1] !== p[1]) cleaned.push(p);
  }
  if (cleaned.length >= 2) {
    const first = cleaned[0];
    const last = cleaned[cleaned.length - 1];
    if (first[0] === last[0] && first[1] === last[1]) cleaned.pop();
  }

  return cleaned;
};

export const computeFieldSplit = (farmPolygon, stations) => {
  if (!farmPolygon || farmPolygon.length < 3 || !stations || stations.length < 4) {
    return null;
  }

  const stationLats = stations.map(s => s.locationLat).slice().sort((a, b) => a - b);
  const stationLons = stations.map(s => s.locationLon).slice().sort((a, b) => a - b);

  const candidates = [
    { axis: 'lon', threshold: (stationLons[1] + stationLons[2]) / 2 },
    { axis: 'lat', threshold: (stationLats[1] + stationLats[2]) / 2 },
  ];

  const totalArea = approximatePolygonAreaM2(farmPolygon);
  const target = totalArea / 2;

  let best = null;
  for (const c of candidates) {
    const polyA = clipPolygonHalfPlane(farmPolygon, c.axis, c.threshold, true);
    const polyB = clipPolygonHalfPlane(farmPolygon, c.axis, c.threshold, false);
    if (polyA.length < 3 || polyB.length < 3) continue;
    const areaA = approximatePolygonAreaM2(polyA);
    const areaB = approximatePolygonAreaM2(polyB);
    const score = Math.abs(areaA - target) + Math.abs(areaB - target);
    if (!best || score < best.score) {
      best = { ...c, polyA, polyB, areaA, areaB, score };
    }
  }
  return best;
};

export const createFieldGroupAreas = (farmPolygon, stations) => {
  const split = computeFieldSplit(farmPolygon, stations);
  if (!split) return null;

  const { axis, threshold, polyA, polyB, areaA, areaB } = split;

  const getCoord = (s) => (axis === 'lon' ? s.locationLon : s.locationLat);
  const stationsA = stations.filter(s => getCoord(s) <= threshold);
  const stationsB = stations.filter(s => getCoord(s) > threshold);

  const mkArea = (areaName, polygon, areaM2, areaStations) => ({
    areaName,
    polygon,
    stations: areaStations,
    bounds: null,
    campoApertoCount: areaStations.filter(s => s.sensorType === 'campo_aperto').length,
    retroModuloCount: areaStations.filter(s => s.sensorType === 'retro_modulo').length,
    totalCount: areaStations.length,
    areaM2,
    areaHa: areaM2 / 10000,
  });

  return {
    'Section A': mkArea('Section A', polyA, areaA, stationsA),
    'Section B': mkArea('Section B', polyB, areaB, stationsB),
  };
};

/**
 * Create polygon coordinates for an area
 */
export const createAreaPolygon = (bounds) => {
  if (!bounds) return null;
  
  return [
    [bounds.minLat, bounds.minLon], // Southwest
    [bounds.minLat, bounds.maxLon], // Southeast
    [bounds.maxLat, bounds.maxLon], // Northeast
    [bounds.maxLat, bounds.minLon], // Northwest
    [bounds.minLat, bounds.minLon]  // Close polygon
  ];
};

/**
 * Get area label text
 */
export const getAreaLabel = (area, t) => {
  const parts = [];
  if (area.campoApertoCount > 0) {
    const label = t ? t('stations.types.campo_aperto') : 'Campo Aperto';
    parts.push(`${area.campoApertoCount} ${label}`);
  }
  if (area.retroModuloCount > 0) {
    const label = t ? t('stations.types.retro_modulo') : 'Retro Modulo';
    parts.push(`${area.retroModuloCount} ${label}`);
  }
  if (area.areaHa) {
    parts.push(`~${area.areaHa.toFixed(2)} ha`);
  }
  return parts.join(', ') || (t ? t('map.areaOverlay.noStations') : 'No stations');
};

// ============================================================================
// Hierarchical grouping helpers (Quadrants/Zones/Sections)
// ============================================================================

const AREA_SECTION_TARGET_HA = 10;

const normalizeNumericString = (value) => {
  if (!value) return '';
  return String(value).replace(/[^0-9.,]/g, '');
};

const parseAreaStringToHa = (value) => {
  if (value == null) return null;
  if (typeof value === 'number' && !Number.isNaN(value)) {
    // Heuristic: if it's a big number, assume m².
    return value > 500 ? value / 10000 : value;
  }
  const raw = String(value).toLowerCase();
  const numeric = normalizeNumericString(raw).replace(/,/g, '');
  const parsed = parseFloat(numeric);
  if (Number.isNaN(parsed)) return null;
  if (raw.includes('ha')) return parsed;
  if (raw.includes('m²') || raw.includes('m2')) return parsed / 10000;
  return parsed > 500 ? parsed / 10000 : parsed;
};

const getPolygonBounds = (polygon) => {
  if (!polygon || polygon.length === 0) return null;
  const lats = polygon.map(point => point[0]);
  const lons = polygon.map(point => point[1]);
  return {
    minLat: Math.min(...lats),
    maxLat: Math.max(...lats),
    minLon: Math.min(...lons),
    maxLon: Math.max(...lons),
  };
};

const getBoundsFromPoints = (points) => {
  if (!points || points.length === 0) return null;
  const lats = points.map((p) => p[0]);
  const lons = points.map((p) => p[1]);
  return {
    minLat: Math.min(...lats),
    maxLat: Math.max(...lats),
    minLon: Math.min(...lons),
    maxLon: Math.max(...lons),
  };
};

const padBounds = (bounds, padding = 0.0003) => {
  if (!bounds) return null;
  return {
    minLat: bounds.minLat - padding,
    maxLat: bounds.maxLat + padding,
    minLon: bounds.minLon - padding,
    maxLon: bounds.maxLon + padding,
  };
};

const CONCAVE_HULL_CONCAVITY = 2.5;
const CONCAVE_HULL_LENGTH_THRESHOLD = 0;

const isValidPoint = (point) => {
  return Array.isArray(point)
    && point.length >= 2
    && Number.isFinite(point[0])
    && Number.isFinite(point[1]);
};

const normalizePolygonPoints = (polygon = []) => {
  const cleaned = polygon.filter(isValidPoint).map(point => [point[0], point[1]]);
  if (cleaned.length >= 2) {
    const first = cleaned[0];
    const last = cleaned[cleaned.length - 1];
    if (first[0] === last[0] && first[1] === last[1]) {
      cleaned.pop();
    }
  }
  return cleaned;
};

const getBoundsCornerPoints = (bounds) => {
  if (!bounds) return [];
  return [
    [bounds.minLat, bounds.minLon],
    [bounds.minLat, bounds.maxLon],
    [bounds.maxLat, bounds.maxLon],
    [bounds.maxLat, bounds.minLon],
  ];
};

const collectSectionPoints = (sections = []) => {
  const points = [];
  const seen = new Set();
  const addUnique = (point) => {
    if (!isValidPoint(point)) return;
    const key = `${point[0]},${point[1]}`;
    if (seen.has(key)) return;
    seen.add(key);
    points.push(point);
  };

  sections.forEach((section) => {
    if (Array.isArray(section?.polygon) && section.polygon.length >= 3) {
      normalizePolygonPoints(section.polygon).forEach(addUnique);
      return;
    }
    if (section?.bounds) {
      getBoundsCornerPoints(section.bounds).forEach(addUnique);
      return;
    }
    const center = getSectionCenterLatLon(section);
    if (center) addUnique(center);
  });

  return points;
};

const buildConcaveHullPolygon = (sections = [], fallbackBounds = null) => {
  const points = collectSectionPoints(sections);
  if (points.length < 3) {
    return fallbackBounds ? createAreaPolygon(fallbackBounds) : null;
  }
  let hull = null;
  try {
    hull = concaveman(
      points.map(([lat, lon]) => [lon, lat]),
      CONCAVE_HULL_CONCAVITY,
      CONCAVE_HULL_LENGTH_THRESHOLD
    );
  } catch (error) {
    hull = null;
  }
  if (!Array.isArray(hull) || hull.length < 3) {
    return fallbackBounds ? createAreaPolygon(fallbackBounds) : null;
  }
  return hull.map(([lon, lat]) => [lat, lon]);
};

export const getFarmAreaHa = (farm, sections = []) => {
  if (farm?.polygon && farm.polygon.length >= 3) {
    const areaM2 = approximatePolygonAreaM2(farm.polygon);
    return areaM2 / 10000;
  }

  if (sections && sections.length > 0) {
    let totalHa = 0;
    let hasArea = false;
    sections.forEach((section) => {
      if (typeof section.areaHa === 'number') {
        totalHa += section.areaHa;
        hasArea = true;
      } else if (section.polygon && section.polygon.length >= 3) {
        totalHa += approximatePolygonAreaM2(section.polygon) / 10000;
        hasArea = true;
      }
    });
    if (hasArea) return totalHa;
  }

  const parsed = parseAreaStringToHa(farm?.area);
  if (parsed != null) return parsed;
  return 0;
};

export const getFarmBounds = (farm, sections = [], stations = []) => {
  if (farm?.bounds) {
    const { north, south, east, west } = farm.bounds;
    if ([north, south, east, west].every(v => typeof v === 'number')) {
      return { minLat: south, maxLat: north, minLon: west, maxLon: east };
    }
  }

  if (farm?.polygon && farm.polygon.length > 0) {
    return getPolygonBounds(farm.polygon);
  }

  if (sections && sections.length > 0) {
    const points = sections.flatMap((section) => {
      if (section.position?.lat != null && section.position?.lon != null) {
        return [[section.position.lat, section.position.lon]];
      }
      if (Array.isArray(section.center) && section.center.length === 2) {
        return [[section.center[0], section.center[1]]];
      }
      if (Array.isArray(section.polygon) && section.polygon.length > 0) {
        return section.polygon;
      }
      return [];
    });
    const bounds = getBoundsFromPoints(points);
    if (bounds) return bounds;
  }

  if (stations && stations.length > 0) {
    const points = stations
      .filter(s => s.locationLat != null && s.locationLon != null)
      .map(s => [s.locationLat, s.locationLon]);
    const bounds = getBoundsFromPoints(points);
    if (bounds) return bounds;
  }

  return null;
};

const getSectionCenterLatLon = (section) => {
  if (section?.position?.lat != null && section?.position?.lon != null) {
    return [section.position.lat, section.position.lon];
  }
  if (Array.isArray(section?.center) && section.center.length === 2) {
    return [section.center[0], section.center[1]];
  }
  if (Array.isArray(section?.polygon) && section.polygon.length > 0) {
    const lat = section.polygon.reduce((sum, p) => sum + p[0], 0) / section.polygon.length;
    const lon = section.polygon.reduce((sum, p) => sum + p[1], 0) / section.polygon.length;
    return [lat, lon];
  }
  return null;
};

const buildSectionsFromStations = (stations = []) => {
  const sectionMap = new Map();

  stations.forEach((station) => {
    const parsed = parseLocationDescription(station.locationDescription);
    const sectionKey = station.sectionId || parsed.fullArea || parsed.area || 'Unknown';
    const sectionName = parsed.fullArea || parsed.area || station.sectionId || 'Unknown';
    const key = sectionKey || sectionName;

    if (!sectionMap.has(key)) {
      sectionMap.set(key, {
        id: key,
        name: sectionName,
        level: 'section',
        areaName: sectionName,
        stations: [],
      });
    }
    sectionMap.get(key).stations.push(station);
  });

  return Array.from(sectionMap.values()).map((section) => {
    const points = section.stations.map(s => [s.locationLat, s.locationLon]).filter(p => p[0] != null);
    const bounds = padBounds(getBoundsFromPoints(points));
    const center = bounds
      ? [(bounds.minLat + bounds.maxLat) / 2, (bounds.minLon + bounds.maxLon) / 2]
      : null;
    const campoApertoCount = section.stations.filter(s => s.sensorType === 'campo_aperto').length;
    const retroModuloCount = section.stations.filter(s => s.sensorType === 'retro_modulo').length;
    return {
      ...section,
      bounds,
      polygon: bounds ? createAreaPolygon(bounds) : null,
      position: center ? { lat: center[0], lon: center[1] } : undefined,
      sectionIds: [section.id],
      campoApertoCount,
      retroModuloCount,
      totalCount: section.stations.length,
    };
  });
};

const attachStationsToSections = (sections = [], stations = []) => {
  const byId = new Map();
  const byName = new Map();
  const byStationId = new Map();
  sections.forEach((section) => {
    if (section.id) byId.set(String(section.id), section);
    if (section.name) byName.set(String(section.name).toLowerCase(), section);
    if (Array.isArray(section.stationIds)) {
      section.stationIds.forEach((stationId) => {
        if (stationId) byStationId.set(String(stationId), section);
      });
    }
  });

  stations.forEach((station) => {
    let section = null;
    if (station.id && byStationId.has(String(station.id))) {
      section = byStationId.get(String(station.id));
    } else if (station.sectionId && byId.has(String(station.sectionId))) {
      section = byId.get(String(station.sectionId));
    } else if (station.sectionId && byName.has(String(station.sectionId).toLowerCase())) {
      section = byName.get(String(station.sectionId).toLowerCase());
    } else if (station.locationDescription) {
      const parsed = parseLocationDescription(station.locationDescription);
      const nameKey = parsed.fullArea || parsed.area;
      if (nameKey && byName.has(String(nameKey).toLowerCase())) {
        section = byName.get(String(nameKey).toLowerCase());
      }
    }
    if (section) {
      if (!section.stations) section.stations = [];
      section.stations.push(station);
    }
  });

  return sections;
};

const normalizeSectionsForGrouping = ({ farm, sections, stations }) => {
  if (!sections || sections.length === 0) {
    return buildSectionsFromStations(stations);
  }

  const normalized = sections.map((section) => {
    const center = getSectionCenterLatLon(section);
    const polygon = Array.isArray(section.polygon) ? section.polygon : null;
    const bounds = polygon ? getPolygonBounds(polygon) : null;
    const areaHa = typeof section.areaHa === 'number'
      ? section.areaHa
      : (polygon ? approximatePolygonAreaM2(polygon) / 10000 : undefined);
    return {
      ...section,
      level: 'section',
      areaName: section.name || section.id,
      position: section.position || (center ? { lat: center[0], lon: center[1] } : undefined),
      polygon,
      bounds,
      areaHa,
      stations: section.stations ? [...section.stations] : [],
      sectionIds: section.id ? [section.id] : [],
    };
  });

  attachStationsToSections(normalized, stations);

  normalized.forEach((section) => {
    if (!section.stations) section.stations = [];
    if (!section.bounds) {
      const points = section.stations
        .filter(s => s.locationLat != null && s.locationLon != null)
        .map(s => [s.locationLat, s.locationLon]);
      if (points.length > 0) {
        section.bounds = padBounds(getBoundsFromPoints(points));
      } else if (section.position) {
        section.bounds = padBounds({
          minLat: section.position.lat,
          maxLat: section.position.lat,
          minLon: section.position.lon,
          maxLon: section.position.lon,
        }, 0.0002);
      }
    }
    if (!section.polygon && section.bounds) {
      section.polygon = createAreaPolygon(section.bounds);
    }

    section.campoApertoCount = section.stations.filter(s => s.sensorType === 'campo_aperto').length;
    section.retroModuloCount = section.stations.filter(s => s.sensorType === 'retro_modulo').length;
    section.totalCount = section.stations.length;
  });

  return normalized;
};

const buildZoneGrid = (bounds, zoneCount) => {
  if (!bounds || zoneCount <= 0) return [];
  const latSpan = Math.max(bounds.maxLat - bounds.minLat, 0.000001);
  const lonSpan = Math.max(bounds.maxLon - bounds.minLon, 0.000001);
  const aspectRatio = lonSpan / latSpan;
  const cols = Math.max(1, Math.ceil(Math.sqrt(zoneCount * aspectRatio)));
  const rows = Math.max(1, Math.ceil(zoneCount / cols));

  const zones = [];
  const latStep = latSpan / rows;
  const lonStep = lonSpan / cols;

  let zoneIndex = 0;
  for (let row = 0; row < rows; row += 1) {
    const rowMaxLat = bounds.maxLat - row * latStep;
    const rowMinLat = bounds.maxLat - (row + 1) * latStep;
    for (let col = 0; col < cols; col += 1) {
      if (zoneIndex >= zoneCount) break;
      const colMinLon = bounds.minLon + col * lonStep;
      const colMaxLon = bounds.minLon + (col + 1) * lonStep;
      const id = `zone-${String(zoneIndex + 1).padStart(2, '0')}`;
      const name = `Zone ${String(zoneIndex + 1).padStart(2, '0')}`;
      const zoneBounds = {
        minLat: rowMinLat,
        maxLat: rowMaxLat,
        minLon: colMinLon,
        maxLon: colMaxLon,
      };
      zones.push({
        id,
        name,
        level: 'zone',
        areaName: name,
        bounds: zoneBounds,
        polygon: createAreaPolygon(zoneBounds),
        center: [(rowMinLat + rowMaxLat) / 2, (colMinLon + colMaxLon) / 2],
        sections: [],
        sectionIds: [],
      });
      zoneIndex += 1;
    }
  }
  return zones;
};

const assignSectionsToZones = (sections, zones) => {
  if (!zones || zones.length === 0) return { zones: [], sectionIdToZoneId: new Map() };
  const sectionIdToZoneId = new Map();
  const zoneList = zones.map(zone => ({ ...zone, sections: [], sectionIds: [], stations: [] }));

  sections.forEach((section) => {
    const center = getSectionCenterLatLon(section) || (section.position ? [section.position.lat, section.position.lon] : null);
    if (!center) return;
    const [lat, lon] = center;
    const zone = zoneList.find((candidate) => {
      return lat <= candidate.bounds.maxLat &&
        lat >= candidate.bounds.minLat &&
        lon >= candidate.bounds.minLon &&
        lon <= candidate.bounds.maxLon;
    }) || zoneList[0];

    zone.sections.push(section);
    if (section.id) zone.sectionIds.push(section.id);
    if (section.stations) zone.stations.push(...section.stations);
    if (section.id) sectionIdToZoneId.set(section.id, zone.id);
  });

  zoneList.forEach((zone) => {
    zone.campoApertoCount = zone.stations.filter(s => s.sensorType === 'campo_aperto').length;
    zone.retroModuloCount = zone.stations.filter(s => s.sensorType === 'retro_modulo').length;
    zone.totalCount = zone.stations.length;
    const hullPolygon = buildConcaveHullPolygon(zone.sections, zone.bounds);
    if (hullPolygon) {
      zone.polygon = hullPolygon;
      const hullBounds = getPolygonBounds(hullPolygon);
      if (hullBounds) {
        zone.bounds = hullBounds;
        zone.center = [(hullBounds.minLat + hullBounds.maxLat) / 2, (hullBounds.minLon + hullBounds.maxLon) / 2];
      }
      zone.areaHa = approximatePolygonAreaM2(hullPolygon) / 10000;
    } else if (zone.polygon) {
      zone.areaHa = approximatePolygonAreaM2(zone.polygon) / 10000;
    }
  });

  return { zones: zoneList, sectionIdToZoneId };
};

const buildQuadrants = (bounds) => {
  if (!bounds) return [];
  const midLat = (bounds.minLat + bounds.maxLat) / 2;
  const midLon = (bounds.minLon + bounds.maxLon) / 2;
  const mkQuadrant = (id, name, minLat, maxLat, minLon, maxLon) => {
    const quadBounds = { minLat, maxLat, minLon, maxLon };
    return {
      id,
      name,
      level: 'quadrant',
      areaName: name,
      bounds: quadBounds,
      polygon: createAreaPolygon(quadBounds),
      center: [(minLat + maxLat) / 2, (minLon + maxLon) / 2],
      zones: [],
      zoneIds: [],
      sections: [],
      sectionIds: [],
      stations: [],
    };
  };

  return [
    mkQuadrant('quadrant-nw', 'Quadrant NW', midLat, bounds.maxLat, bounds.minLon, midLon),
    mkQuadrant('quadrant-ne', 'Quadrant NE', midLat, bounds.maxLat, midLon, bounds.maxLon),
    mkQuadrant('quadrant-sw', 'Quadrant SW', bounds.minLat, midLat, bounds.minLon, midLon),
    mkQuadrant('quadrant-se', 'Quadrant SE', bounds.minLat, midLat, midLon, bounds.maxLon),
  ];
};

const assignZonesToQuadrants = (zones, quadrants) => {
  if (!quadrants || quadrants.length === 0) return { quadrants: [], zoneIdToQuadrantId: new Map() };
  const zoneIdToQuadrantId = new Map();
  const quadrantsList = quadrants.map(quad => ({ ...quad, zones: [], zoneIds: [], sections: [], sectionIds: [], stations: [] }));

  zones.forEach((zone) => {
    const center = zone.center || (zone.bounds ? [
      (zone.bounds.minLat + zone.bounds.maxLat) / 2,
      (zone.bounds.minLon + zone.bounds.maxLon) / 2,
    ] : null);
    if (!center) return;
    const [lat, lon] = center;
    const quad = quadrantsList.find((candidate) => {
      return lat <= candidate.bounds.maxLat &&
        lat >= candidate.bounds.minLat &&
        lon >= candidate.bounds.minLon &&
        lon <= candidate.bounds.maxLon;
    }) || quadrantsList[0];

    quad.zones.push(zone);
    if (zone.id) quad.zoneIds.push(zone.id);
    if (zone.sections) quad.sections.push(...zone.sections);
    if (zone.sectionIds) quad.sectionIds.push(...zone.sectionIds);
    if (zone.stations) quad.stations.push(...zone.stations);
    if (zone.id) zoneIdToQuadrantId.set(zone.id, quad.id);
  });

  quadrantsList.forEach((quad) => {
    quad.campoApertoCount = quad.stations.filter(s => s.sensorType === 'campo_aperto').length;
    quad.retroModuloCount = quad.stations.filter(s => s.sensorType === 'retro_modulo').length;
    quad.totalCount = quad.stations.length;
    const hullPolygon = buildConcaveHullPolygon(quad.sections, quad.bounds);
    if (hullPolygon) {
      quad.polygon = hullPolygon;
      const hullBounds = getPolygonBounds(hullPolygon);
      if (hullBounds) {
        quad.bounds = hullBounds;
        quad.center = [(hullBounds.minLat + hullBounds.maxLat) / 2, (hullBounds.minLon + hullBounds.maxLon) / 2];
      }
      quad.areaHa = approximatePolygonAreaM2(hullPolygon) / 10000;
    } else if (quad.polygon) {
      quad.areaHa = approximatePolygonAreaM2(quad.polygon) / 10000;
    }
  });

  return { quadrants: quadrantsList, zoneIdToQuadrantId };
};

const filterUnusedZones = (zones = []) => {
  return zones.filter(zone => Array.isArray(zone.sections) && zone.sections.length > 0);
};

const filterUnusedQuadrants = (quadrants = []) => {
  return quadrants.filter(quadrant => Array.isArray(quadrant.zones) && quadrant.zones.length > 0);
};

export const buildFarmGrouping = ({ farm, sections = [], stations = [] }) => {
  const normalizedSections = normalizeSectionsForGrouping({ farm, sections, stations });
  const shouldFilterEmptySections = Array.isArray(farm?.excludedStationNumbers)
    && farm.excludedStationNumbers.length > 0;
  const groupedSections = shouldFilterEmptySections
    ? normalizedSections.filter(section => (section.stations || []).length > 0)
    : normalizedSections;
  const areaHa = getFarmAreaHa(farm, groupedSections);
  const bounds = getFarmBounds(farm, groupedSections, stations);

  let groupingLevel = 'section';
  if (areaHa > 40) {
    groupingLevel = 'quadrant';
  } else if (areaHa > 10) {
    groupingLevel = 'zone';
  }

  if (!bounds) {
    return {
      areaHa,
      groupingLevel: 'section',
      bounds: null,
      sections: groupedSections,
      zones: [],
      quadrants: [],
      sectionIdToZoneId: new Map(),
      zoneIdToQuadrantId: new Map(),
    };
  }

  if (groupingLevel === 'section') {
    return {
      areaHa,
      groupingLevel,
      bounds,
      sections: groupedSections,
      zones: [],
      quadrants: [],
      sectionIdToZoneId: new Map(),
      zoneIdToQuadrantId: new Map(),
    };
  }

  const zoneCount = Math.max(1, Math.ceil(areaHa / AREA_SECTION_TARGET_HA));
  const baseZones = buildZoneGrid(bounds, zoneCount);
  const { zones: rawZones, sectionIdToZoneId } = assignSectionsToZones(groupedSections, baseZones);
  const zones = filterUnusedZones(rawZones);

  if (groupingLevel === 'zone') {
    return {
      areaHa,
      groupingLevel,
      bounds,
      sections: groupedSections,
      zones,
      quadrants: [],
      sectionIdToZoneId,
      zoneIdToQuadrantId: new Map(),
    };
  }

  const baseQuadrants = buildQuadrants(bounds);
  const { quadrants: rawQuadrants, zoneIdToQuadrantId } = assignZonesToQuadrants(zones, baseQuadrants);
  const quadrants = filterUnusedQuadrants(rawQuadrants);

  return {
    areaHa,
    groupingLevel,
    bounds,
    sections: groupedSections,
    zones,
    quadrants,
    sectionIdToZoneId,
    zoneIdToQuadrantId,
  };
};

export const getSectionIdsForGroup = (group) => {
  if (!group) return [];
  const directIds = Array.isArray(group.sectionIds) ? group.sectionIds.filter(Boolean) : [];
  if (directIds.length > 0) return directIds;
  const sectionIds = Array.isArray(group.sections)
    ? group.sections.map(section => section?.id).filter(Boolean)
    : [];
  if (sectionIds.length > 0) return sectionIds;
  if (Array.isArray(group.zones)) {
    return group.zones.flatMap(zone => getSectionIdsForGroup(zone));
  }
  return group.id ? [group.id] : [];
};

export const getSectionIdsForDrill = (grouping, drillState) => {
  if (!grouping || !drillState || !drillState.level || !drillState.id) {
    return [];
  }
  if (drillState.level === 'section') {
    return [drillState.id];
  }
  if (drillState.level === 'zone') {
    const zone = grouping.zones.find(candidate => candidate.id === drillState.id);
    return getSectionIdsForGroup(zone);
  }
  if (drillState.level === 'quadrant') {
    const quadrant = grouping.quadrants.find(candidate => candidate.id === drillState.id);
    return getSectionIdsForGroup(quadrant);
  }
  return [];
};

// Resolve which analytics "section(s)" should be selected for a given station.
// - Split mode: sections have `stationIds` -> select the section that contains the station.
// - Legacy mode: section.id === station.id -> select that one section.
export const resolveSectionIdsForStation = (farm, stationId, station, sectionsOverride = null) => {
  const sections = Array.isArray(sectionsOverride) ? sectionsOverride : (farm?.sections || []);
  if (!stationId || sections.length === 0) return [];

  const matches = [];
  for (const section of sections) {
    if (!section) continue;
    if (section.id === stationId) {
      matches.push(section.id);
      continue;
    }
    if (Array.isArray(section.stationIds) && section.stationIds.includes(stationId)) {
      matches.push(section.id);
    }
  }

  // Last-resort: try matching by "Section X" label if present.
  if (matches.length === 0 && station?.locationDescription) {
    const m = station.locationDescription.match(/Section\s+([A-Z0-9]+)/i);
    if (m) {
      const normalized = `Section ${m[1].toUpperCase()}`;
      const byName = sections.find(s => (s?.name || '').toLowerCase() === normalized.toLowerCase());
      if (byName?.id) matches.push(byName.id);
    }
  }

  return matches;
};
